/**
 * Exit Interview List & Viewer — HR Manager / Admin
 * Route: /offboarding/exit-interviews  (add to App.js with ADMIN_HR roles)
 * Add to DashboardLayout under Offboarding section
 *
 * Features:
 * - List all submitted exit interviews
 * - Click any row → view full answers in modal
 * - Analytics: avg ratings, top reasons, would-return breakdown
 * - Bell notification click → opens this page automatically
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Table, Badge, Button, Modal,
    Spinner, Alert, ProgressBar,
} from 'react-bootstrap';
import {
    LogOut, Star, RefreshCw, Eye, BarChart2,
    CheckCircle, XCircle, Users, TrendingUp,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const fmtDate = (d) => !d ? '—'
    : new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });

const StarDisplay = ({ value, max = 5 }) => (
    <span>
        {[...Array(max)].map((_, i) => (
            <Star key={i} size={14}
                fill={i < Math.round(value) ? '#f59e0b' : 'none'}
                color={i < Math.round(value) ? '#f59e0b' : '#d1d5db'}
                style={{marginRight:1}}/>
        ))}
        <span className="ms-1 small text-muted">({parseFloat(value || 0).toFixed(1)})</span>
    </span>
);

const NPSBar = ({ value, max = 10 }) => {
    const pct = max > 0 ? (parseFloat(value || 0) / max) * 100 : 0;
    const color = pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'danger';
    return (
        <div className="d-flex align-items-center gap-2">
            <ProgressBar now={pct} variant={color} style={{height:6, flex:1}}/>
            <span className="small fw-bold" style={{minWidth:32}}>
                {parseFloat(value || 0).toFixed(1)}/{max}
            </span>
        </div>
    );
};

// ── Section/Question definitions (mirrors ExitInterview.js) ─────────────────
const QUESTION_LABELS = {
    primary_reason:           'Primary reason for leaving',
    secondary_reason:         'Secondary reason',
    would_return:             'Would consider returning?',
    role_clarity:             'Role & responsibilities clarity (1–10)',
    workload:                 'Workload rating',
    skills_used:              'Skills & abilities utilized (1–10)',
    job_satisfaction_comment: 'Comments about role',
    manager_support:          'Manager support & guidance (stars)',
    manager_feedback:         'Received regular feedback from manager?',
    management_comment:       'Feedback for management',
    culture_rating:           'Overall company culture (1–10)',
    team_collaboration:       'Team collaboration (1–10)',
    dei_rating:               'Felt included & respected (1–10)',
    culture_comment:          'What to change about culture?',
    comp_satisfaction:        'Compensation satisfaction (1–10)',
    benefits_satisfaction:    'Benefits package satisfaction (1–10)',
    recommend_company:        'Likelihood to recommend company (1–10)',
    biggest_achievement:      'Biggest achievement',
    suggestions:              'Suggestions for improvement',
    overall_experience:       'Overall experience (stars)',
};

const renderAnswer = (key, value) => {
    if (!value && value !== 0) return <span className="text-muted small">Not answered</span>;
    // Star questions
    if (['manager_support','overall_experience'].includes(key)) {
        return <StarDisplay value={value}/>;
    }
    // NPS questions (1-10)
    if (['role_clarity','skills_used','culture_rating','team_collaboration',
         'dei_rating','comp_satisfaction','benefits_satisfaction','recommend_company'].includes(key)) {
        return <NPSBar value={value} max={10}/>;
    }
    // Boolean/short text
    if (['manager_feedback','would_return'].includes(key)) {
        const isPos = value === 'Yes' || value === true;
        const isNeg = value === 'No'  || value === false;
        return (
            <Badge bg={isPos ? 'success' : isNeg ? 'danger' : 'secondary'}>
                {isPos ? <><CheckCircle size={10} className="me-1"/>Yes</>
                    : isNeg ? <><XCircle size={10} className="me-1"/>No</>
                    : value}
            </Badge>
        );
    }
    // Long text
    return <p className="mb-0 small" style={{whiteSpace:'pre-wrap'}}>{value}</p>;
};

// ════════════════════════════════════════════════════════════════════════════
const ExitInterviewList = () => {
    const [interviews, setInterviews]   = useState([]);
    const [analytics,  setAnalytics]    = useState(null);
    const [loading,    setLoading]       = useState(true);
    const [selected,   setSelected]     = useState(null);
    const [showModal,  setShowModal]    = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [view,       setView]         = useState('list'); // 'list' | 'analytics'
    const [total,      setTotal]        = useState(0);
    const [page,       setPage]         = useState(1);
    const LIMIT = 20;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [listRes, analyticsRes] = await Promise.allSettled([
                axiosInstance.get('/offboarding/exit-interviews', {
                    params: { page, limit: LIMIT }
                }),
                axiosInstance.get('/offboarding/exit-interview-analytics'),
            ]);
            if (listRes.status === 'fulfilled') {
                const data = listRes.value.data.data;
                setInterviews(data?.interviews || []);
                setTotal(data?.total || 0);
            }
            if (analyticsRes.status === 'fulfilled') {
                setAnalytics(analyticsRes.value.data.data);
            }
        } catch (err) {
            toast.error('Failed to load exit interviews');
        } finally { setLoading(false); }
    }, [page]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (interview) => {
        // Use employee_id if available, else use the record id
        const empId = interview.employee_id || interview.id;
        if (!empId) { toast.error('Cannot load interview — missing employee ID'); return; }
        setLoadingDetail(true);
        setShowModal(true);
        try {
            const res = await axiosInstance.get(`/offboarding/exit-interview/${empId}`);
            setSelected(res.data.data);
        } catch {
            toast.error('Failed to load interview details');
            setShowModal(false);
        } finally { setLoadingDetail(false); }
    };

    const totalPages = Math.ceil(total / LIMIT);
    const summary    = analytics?.summary?.[0] || {};
    const reasons    = analytics?.top_reasons || [];

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <LogOut size={22} className="text-primary"/>
                        Exit Interviews
                    </h4>
                    <p className="text-muted small mb-0">
                        {total} submission{total !== 1 ? 's' : ''} — confidential employee feedback
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button size="sm"
                        variant={view === 'list' ? 'primary' : 'outline-secondary'}
                        onClick={() => setView('list')}>
                        <Users size={13} className="me-1"/>List
                    </Button>
                    <Button size="sm"
                        variant={view === 'analytics' ? 'primary' : 'outline-secondary'}
                        onClick={() => setView('analytics')}>
                        <BarChart2 size={13} className="me-1"/>Analytics
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={load}>
                        <RefreshCw size={13}/>
                    </Button>
                </div>
            </div>

            {/* ── ANALYTICS VIEW ─────────────────────────────────────── */}
            {view === 'analytics' && (
                <>
                    {/* Summary cards */}
                    <Row className="g-3 mb-4">
                        {[
                            { label:'Total Interviews',    value: summary.total_interviews || 0,              color:'#4f46e5', suffix:'' },
                            { label:'Avg Overall Rating',  value: parseFloat(summary.avg_overall_rating||0).toFixed(1), color:'#f59e0b', suffix:'/5 ⭐' },
                            { label:'Would Recommend',     value: parseFloat(summary.avg_recommend_score||0).toFixed(1), color:'#059669', suffix:'/10' },
                            { label:'Would Return (Yes)',  value: summary.would_return_yes || 0,              color:'#0891b2', suffix:'' },
                        ].map(({ label, value, color, suffix }) => (
                            <Col xs={6} md={3} key={label}>
                                <Card className="border-0 shadow-sm text-center p-3">
                                    <div style={{ fontSize:28, fontWeight:800, color }}>{value}{suffix}</div>
                                    <div className="text-muted small">{label}</div>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <Row className="g-4 mb-4">
                        {/* Average ratings */}
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold mb-4">Average Ratings</h6>
                                    {[
                                        { label:'Overall Experience',   value: summary.avg_overall_rating, max:5,  stars:true },
                                        { label:'Manager Support',       value: summary.avg_manager_rating, max:5,  stars:true },
                                        { label:'Company Culture',       value: summary.avg_culture_rating, max:10 },
                                        { label:'Compensation',          value: summary.avg_comp_rating,    max:10 },
                                        { label:'Recommend Company',     value: summary.avg_recommend_score, max:10 },
                                    ].map(({ label, value, max, stars }) => (
                                        <div key={label} className="mb-3">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>{label}</span>
                                            </div>
                                            {stars
                                                ? <StarDisplay value={value} max={max}/>
                                                : <NPSBar value={value} max={max}/>
                                            }
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Top reasons */}
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold mb-4">Top Reasons for Leaving</h6>
                                    {reasons.length === 0 && (
                                        <p className="text-muted small">No data yet</p>
                                    )}
                                    {reasons.map((r, i) => (
                                        <div key={i} className="mb-3">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>{r.primary_reason || 'Not specified'}</span>
                                                <Badge bg="secondary" style={{fontSize:10}}>{r.count}</Badge>
                                            </div>
                                            <ProgressBar
                                                now={total > 0 ? (r.count / total) * 100 : 0}
                                                variant="primary"
                                                style={{height:6}}/>
                                        </div>
                                    ))}

                                    <hr/>
                                    <h6 className="fw-bold mb-3">Would Return?</h6>
                                    <div className="d-flex gap-3">
                                        {[
                                            { label:'Yes',   value:summary.would_return_yes,   color:'success' },
                                            { label:'Maybe', value:summary.would_return_maybe, color:'warning' },
                                            { label:'No',    value:summary.would_return_no,    color:'danger'  },
                                        ].map(({ label, value, color }) => (
                                            <div key={label} className="text-center flex-fill p-2 rounded"
                                                style={{background:'#f8fafc'}}>
                                                <div className={`fw-bold text-${color}`} style={{fontSize:22}}>
                                                    {value || 0}
                                                </div>
                                                <div className="text-muted" style={{fontSize:11}}>{label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* ── LIST VIEW ──────────────────────────────────────────── */}
            {view === 'list' && (
                <Card className="border-0 shadow-sm">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary"/>
                        </div>
                    ) : interviews.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <LogOut size={36} className="mb-2 opacity-30"/>
                            <p>No exit interviews submitted yet.</p>
                            <small>When an employee submits their exit interview, it will appear here.</small>
                        </div>
                    ) : (
                        <>
                            <Table hover responsive className="mb-0 align-middle small">
                                <thead className="bg-light">
                                    <tr className="text-uppercase text-muted" style={{fontSize:10}}>
                                        <th className="ps-4">Employee</th>
                                        <th>Department</th>
                                        <th>Reason for Leaving</th>
                                        <th>Overall</th>
                                        <th>Recommend</th>
                                        <th>Would Return</th>
                                        <th>Submitted</th>
                                        <th className="pe-4">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {interviews.map((ei, i) => (
                                        <tr key={i}>
                                            <td className="ps-4">
                                                <div className="fw-medium">{ei.employee_name}</div>
                                                <div className="text-muted" style={{fontSize:10}}>
                                                    {ei.employee_code} · {ei.designation_name || ''}
                                                </div>
                                            </td>
                                            <td>{ei.department_name || '—'}</td>
                                            <td style={{maxWidth:160}}>
                                                <span className="text-muted" style={{fontSize:11}}>
                                                    {ei.primary_reason || '—'}
                                                </span>
                                            </td>
                                            <td>
                                                {ei.overall_rating ? (
                                                    <StarDisplay value={ei.overall_rating} max={5}/>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                {ei.recommend_score ? (
                                                    <Badge bg={
                                                        ei.recommend_score >= 8 ? 'success' :
                                                        ei.recommend_score >= 5 ? 'warning' : 'danger'
                                                    } style={{fontSize:10}}>
                                                        {ei.recommend_score}/10
                                                    </Badge>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                {ei.would_return ? (
                                                    <Badge bg={
                                                        ei.would_return === 'Yes' ? 'success' :
                                                        ei.would_return === 'No'  ? 'danger' : 'secondary'
                                                    } style={{fontSize:10}}>
                                                        {ei.would_return}
                                                    </Badge>
                                                ) : '—'}
                                            </td>
                                            <td className="text-muted" style={{fontSize:11}}>
                                                {fmtDate(ei.submitted_at)}
                                            </td>
                                            <td className="pe-4">
                                                <Button size="sm" variant="outline-primary"
                                                    className="py-0 px-2"
                                                    onClick={() => openDetail(ei)}>
                                                    <Eye size={13} className="me-1"/>View
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
                                    <span className="text-muted small">
                                        Showing {(page-1)*LIMIT+1}–{Math.min(page*LIMIT,total)} of {total}
                                    </span>
                                    <div className="d-flex gap-1">
                                        <Button size="sm" variant="outline-secondary"
                                            disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹</Button>
                                        {[...Array(Math.min(5,totalPages))].map((_,i)=>{
                                            const pg = Math.max(1,Math.min(page-2,totalPages-4))+i;
                                            return (
                                                <Button key={pg} size="sm"
                                                    variant={pg===page?'primary':'outline-secondary'}
                                                    onClick={()=>setPage(pg)}>{pg}</Button>
                                            );
                                        })}
                                        <Button size="sm" variant="outline-secondary"
                                            disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>›</Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card>
            )}

            {/* ── Detail Modal ─────────────────────────────────────────── */}
            <Modal show={showModal} onHide={()=>setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                        <LogOut size={16} className="text-primary"/>
                        Exit Interview — {selected?.employee_name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {loadingDetail ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary"/>
                        </div>
                    ) : selected ? (
                        <>
                            {/* Employee summary bar */}
                            <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-3"
                                style={{background:'#f8fafc'}}>
                                <div style={{
                                    width:44,height:44,borderRadius:'50%',
                                    background:'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                    display:'flex',alignItems:'center',justifyContent:'center',
                                    color:'#fff',fontWeight:700,fontSize:16,flexShrink:0,
                                }}>
                                    {(selected.employee_name||'?')[0]}
                                </div>
                                <div>
                                    <div className="fw-bold">{selected.employee_name}</div>
                                    <div className="text-muted small">
                                        {selected.employee_code}
                                        {selected.department_name ? ` · ${selected.department_name}` : ''}
                                        {' · Submitted: '}{fmtDate(selected.submitted_at)}
                                    </div>
                                </div>
                                {selected.overall_rating && (
                                    <div className="ms-auto">
                                        <StarDisplay value={selected.overall_rating} max={5}/>
                                    </div>
                                )}
                            </div>

                            {/* All answers */}
                            {selected.answers ? (
                                Object.entries(QUESTION_LABELS).map(([key, label]) => {
                                    const val = selected.answers[key];
                                    if (!val && val !== 0) return null;
                                    return (
                                        <div key={key} className="mb-4 pb-3"
                                            style={{borderBottom:'1px solid #f1f5f9'}}>
                                            <div className="fw-bold small text-muted mb-2"
                                                style={{textTransform:'uppercase',letterSpacing:0.5,fontSize:10}}>
                                                {label}
                                            </div>
                                            {renderAnswer(key, val)}
                                        </div>
                                    );
                                })
                            ) : (
                                // Fallback to stored summary fields if answers_json not available
                                <Alert variant="info" className="small">
                                    Detailed answers not available — showing summary data only.
                                    {selected.primary_reason && (
                                        <div className="mt-2">
                                            <strong>Reason:</strong> {selected.primary_reason}
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </>
                    ) : (
                        <p className="text-muted text-center">No data available</p>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={()=>setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ExitInterviewList;
